/**
 * Apartment Property Type Handler
 * 
 * Specific implementation for Apartment properties
 * Apartments are typically units within larger buildings
 */

import { PropertyType } from '../../../shared/definitions/property';
import { ResidentialPropertyHandler } from '../base/residential-property-handler.abstract';
import { RoomTypeConfig, FeatureConfig, FeatureCategory } from '@core/services/property-analysis/property-room-config.service';

export class ApartmentHandler extends ResidentialPropertyHandler {
  readonly propertyType = PropertyType.Apartment;
  readonly displayName = 'Apartment';
  readonly pluralName = 'Apartments';
  readonly icon = 'apartment';
  readonly description = 'Residential unit within a larger building or complex';

  /**
   * Apartments typically have bedrooms, bathrooms, and sometimes balconies
   * But not full houses features like gardens or large garages
   */
  getRoomTypes(): RoomTypeConfig[] {
    const coreRooms = this.getCoreResidentialRoomTypes();
    
    // Apartments can also have:
    const apartmentSpecificRooms: RoomTypeConfig[] = [
      {
        typeKey: 'balcony_terrace',
        label: 'properties.roomTypes.balcony_terrace.label',
        pluralLabel: 'properties.roomTypes.balcony_terrace.plural',
        countField: 'balconies',
        icon: 'balcony',
        defaultCount: 0,
        description: 'properties.roomTypes.balcony_terrace.description',
        formFields: [
          {
            name: 'name',
            label: 'properties.fields.name',
            type: 'text',
            placeholder: 'properties.fieldPlaceholders.balconyName'
          },
          {
            name: 'sqm',
            label: 'properties.fields.surfaceArea',
            type: 'number',
            suffix: 'm²',
            min: 1,
            max: 100,
            step: 0.1
          },
          {
            name: 'type',
            label: 'properties.fields.type',
            type: 'select',
            options: [
              { value: 'balcony', label: 'properties.fieldOptions.balconyType.balcony' },
              { value: 'terrace', label: 'properties.fieldOptions.balconyType.terrace' },
              { value: 'loggia', label: 'properties.fieldOptions.balconyType.loggia' }
            ]
          },
          {
            name: 'orientation',
            label: 'properties.fields.orientation',
            type: 'select',
            options: [
              { value: 'north', label: 'properties.fieldOptions.orientation.north' },
              { value: 'south', label: 'properties.fieldOptions.orientation.south' },
              { value: 'east', label: 'properties.fieldOptions.orientation.east' },
              { value: 'west', label: 'properties.fieldOptions.orientation.west' }
            ]
          },
          {
            name: 'hasCover',
            label: 'properties.fields.hasCoverRoof',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasWaterConnection',
            label: 'properties.fields.waterConnection',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasElectricity',
            label: 'properties.fields.electricity',
            type: 'checkbox',
            default: false
          },
          {
            name: 'notes',
            label: 'properties.fields.additionalNotes',
            type: 'textarea'
          }
        ]
      }
    ];

    return [...coreRooms, ...apartmentSpecificRooms];
  }

  /**
   * Features typical for apartments
   */
  getFeatures(): FeatureConfig[] {
    const coreFeatures = this.getCoreResidentialFeatures();
    
    // Remove features that don't apply to apartments
    const filteredFeatures = coreFeatures.filter(f => 
      !['pool', 'garden'].includes(f.key) // Apartments typically don't have private pools/gardens
    );

    // Add apartment-specific features
    const apartmentSpecificFeatures: FeatureConfig[] = [
      {
        key: 'elevator',
        label: 'properties.featureTypes.elevator.label',
        icon: 'elevator',
        category: FeatureCategory.BUILDING_ONLY, // Always building-level
        description: 'properties.featureTypes.elevator.description',
        detailsInterface: 'BuildingSystems',
        formFields: [
          {
            name: 'numberOfElevators',
            label: 'properties.fields.numberOfElevators',
            type: 'number',
            required: true,
            min: 1,
            default: 1
          },
          {
            name: 'maxFloorNumber',
            label: 'properties.fields.maximumFloor',
            type: 'number',
            min: 1
          },
          {
            name: 'capacityKg',
            label: 'properties.fields.capacity',
            type: 'number',
            suffix: 'kg',
            min: 1
          },
          {
            name: 'hasEmergencyPhone',
            label: 'properties.fields.emergencyPhone',
            type: 'checkbox',
            default: true
          }
        ]
      },
      {
        key: 'storage',
        label: 'properties.featureTypes.storage.label',
        icon: 'inventory_2',
        category: FeatureCategory.FLEXIBLE, // Can be private or building storage
        description: 'properties.featureTypes.storage.description',
        detailsInterface: 'StorageDetail',
        formFields: [
          {
            name: 'storageType',
            label: 'properties.fields.storageType',
            type: 'select',
            required: true,
            options: [
              { value: 'cellar', label: 'properties.fieldOptions.storageType.cellar' },
              { value: 'attic', label: 'properties.fieldOptions.storageType.attic' },
              { value: 'box_room', label: 'properties.fieldOptions.storageType.box_room' },
              { value: 'bike_storage', label: 'properties.fieldOptions.storageType.bike_storage' }
            ]
          },
          {
            name: 'sqm',
            label: 'properties.fields.surfaceArea',
            type: 'number',
            suffix: 'm²',
            min: 1
          },
          {
            name: 'location',
            label: 'properties.fields.location',
            type: 'select',
            options: [
              { value: 'basement', label: 'properties.fieldOptions.storageLocation.basement' },
              { value: 'attic', label: 'properties.fieldOptions.storageLocation.attic' },
              { value: 'floor', label: 'properties.fieldOptions.storageLocation.floor' },
              { value: 'external', label: 'properties.fieldOptions.storageLocation.external' }
            ]
          },
          {
            name: 'hasElectricity',
            label: 'properties.fields.hasElectricity',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasWindow',
            label: 'properties.fields.hasWindow',
            type: 'checkbox',
            default: false
          }
        ]
      },
      {
        key: 'concierge',
        label: 'properties.featureTypes.concierge.label',
        icon: 'support_agent',
        category: FeatureCategory.BUILDING_ONLY, // Building service
        description: 'properties.featureTypes.concierge.description',
        detailsInterface: 'BuildingSystems',
        formFields: [
          {
            name: 'serviceType',
            label: 'properties.fields.serviceType',
            type: 'select',
            options: [
              { value: '24_7', label: 'properties.fieldOptions.conciergeServiceType.24_7' },
              { value: 'business_hours', label: 'properties.fieldOptions.conciergeServiceType.business_hours' },
              { value: 'virtual', label: 'properties.fieldOptions.conciergeServiceType.virtual' }
            ]
          },
          {
            name: 'services',
            label: 'properties.fields.servicesOffered',
            type: 'textarea',
            placeholder: 'properties.fieldPlaceholders.conciergeServices'
          }
        ]
      },
      {
        key: 'office',
        label: 'properties.featureTypes.homeOffice.label',
        icon: 'work',
        category: FeatureCategory.PROPERTY_ONLY,
        description: 'properties.featureTypes.homeOffice.description',
        detailsInterface: 'OfficeDetail',
        formFields: [
          {
            name: 'sqm',
            label: 'properties.fields.surfaceArea',
            type: 'number',
            suffix: 'm²',
            min: 5
          },
          {
            name: 'hasBuiltInFurniture',
            label: 'properties.fields.builtInFurniture',
            type: 'checkbox',
            default: false
          },
          {
            name: 'networkPoints',
            label: 'properties.fields.networkPoints',
            type: 'number',
            min: 0,
            max: 10
          },
          {
            name: 'hasWindow',
            label: 'properties.fields.hasWindow',
            type: 'checkbox',
            default: false
          }
        ]
      }
    ];

    return [...filteredFeatures, ...apartmentSpecificFeatures];
  }

  override getMainCharacteristics(): string[] {
    return ['bedrooms', 'bathrooms', 'surface', 'floor', 'balconies', 'elevator'];
  }

  override getRecommendedMinSurface(): number {
    return 25; // Minimum recommended surface for an apartment
  }

  override getPriceRangeMultiplier(): number {
    return 1.0; // Base price multiplier
  }
}

